import { useCallback, useSyncExternalStore } from "react";

/**
 * Light, dark, or whatever the machine says — for the whole application.
 *
 * The choice is a cookie on the parent domain, exactly like the locale, so
 * switching here follows the reader to ondit.ai and back. The name and the
 * three values are duplicated in `site/src/components/theme-toggle.tsx`; they
 * have to agree, and they are eight lines rather than a shared package because
 * the two surfaces read the cookie at different times — the site stamps the
 * attribute from an inline script before it paints, the panel from React.
 *
 * The Agent is a module of this application rather than a second one, so its
 * components read the theme from here too. That is why the store is a
 * module-level subscription instead of a provider:
 * there is no single place in the tree that is above every reader, and a second
 * provider is a second answer.
 */

export type Theme = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

export const THEME_COOKIE = "ONDIT_THEME";

/** The order the panel's toggle walks. */
const THEMES: Theme[] = ["light", "dark", "system"];

const DARK_QUERY = "(prefers-color-scheme: dark)";

/** The stored choice, or `null` when this browser has never made one. */
export function readStoredTheme(): Theme | null {
	if (typeof document === "undefined") return null;
	const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${THEME_COOKIE}=([^;]+)`));
	const raw = match ? decodeURIComponent(match[1]) : "";
	return raw === "light" || raw === "dark" || raw === "system" ? raw : null;
}

export function readTheme(): Theme {
	return readStoredTheme() ?? "system";
}

function systemTheme(): ResolvedTheme {
	if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
	return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
}

function resolveTheme(theme: Theme): ResolvedTheme {
	return theme === "system" ? systemTheme() : theme;
}

/**
 * Stamps the root element in both conventions, because both stylesheets are
 * loaded in one document and neither can read the other's.
 *
 * `@ondit/tokens` keys off `data-theme`, with `prefers-color-scheme` answering
 * whenever the attribute is absent — so `system` writes no attribute at all and
 * the media query stays live, which is what lets the palette follow the machine
 * with no JavaScript running. The Agent's `globals.css` keys off a `.dark` or
 * `.light` class (`@custom-variant dark`) and has no media-query fallback, so
 * for it `system` has to be resolved to a concrete class here. The two do not
 * contradict each other: the class is always the value the tokens' media query
 * is about to compute, and `subscribe` below re-stamps it when the machine
 * changes its mind.
 *
 * `color-scheme` follows the class for the same reason — the tokens declare it
 * per palette, but form controls and scrollbars inside the Agent's subtree are
 * styled by the class layer, and an inline value is the only one both agree on.
 */
export function apply(theme: Theme): void {
	if (typeof document === "undefined") return;
	const root = document.documentElement;
	const resolved = resolveTheme(theme);
	if (theme === "system") root.removeAttribute("data-theme");
	else root.setAttribute("data-theme", theme);
	root.classList.toggle("dark", resolved === "dark");
	root.classList.toggle("light", resolved !== "dark");
	root.style.colorScheme = resolved;
}

/**
 * Repaints without animating the palette.
 *
 * Every transitioned property on the page would otherwise travel to its new
 * colour at once, which reads as the interface melting rather than switching.
 * The style has to be in the document before the classes change and gone by the
 * next frame; the computed-style read between the two is what forces the first,
 * because style recalculation is otherwise deferred and both mutations would
 * land in the same batch.
 */
function restyle(theme: Theme): void {
	if (typeof document === "undefined") {
		apply(theme);
		return;
	}
	const suppress = document.createElement("style");
	suppress.appendChild(document.createTextNode("*,*::before,*::after{transition:none!important}"));
	document.head.appendChild(suppress);
	apply(theme);
	void window.getComputedStyle(suppress).opacity;
	document.head.removeChild(suppress);
}

export function writeTheme(theme: Theme): void {
	const host = window.location.hostname;
	const domain = host.endsWith(".ondit.ai") || host === "ondit.ai" ? "; domain=.ondit.ai" : "";
	// Conditional rather than always on: Safari will not store a Secure cookie
	// from http://localhost, and that is where the panel is developed.
	const secure = window.location.protocol === "https:" ? "; Secure" : "";
	// biome-ignore lint/suspicious/noDocumentCookie: parent-domain cookie, shared with the directory site
	document.cookie = `${THEME_COOKIE}=${theme}; path=/; max-age=31536000; SameSite=Lax${domain}${secure}`;
	restyle(theme);
	for (const listener of listeners) listener();
}

interface Snapshot {
	theme: Theme;
	systemTheme: ResolvedTheme;
	resolvedTheme: ResolvedTheme;
}

const listeners = new Set<() => void>();
let snapshot: Snapshot = { theme: "system", systemTheme: "light", resolvedTheme: "light" };

// Re-read rather than trust a cached value: the pre-paint script in index.html
// stamps the document before this module is even parsed, and the cookie can
// also have been written by the marketing site. Returning the same object when
// nothing changed is what `useSyncExternalStore` requires.
function getSnapshot(): Snapshot {
	const theme = readTheme();
	const system = systemTheme();
	const resolved = theme === "system" ? system : theme;
	if (snapshot.theme !== theme || snapshot.systemTheme !== system || snapshot.resolvedTheme !== resolved)
		snapshot = { theme, systemTheme: system, resolvedTheme: resolved };
	return snapshot;
}

let watching = false;

function subscribe(listener: () => void): () => void {
	listeners.add(listener);
	if (!watching && typeof window !== "undefined" && typeof window.matchMedia === "function") {
		const media = window.matchMedia(DARK_QUERY);
		if (typeof media.addEventListener === "function") {
			watching = true;
			// The tokens follow the machine on their own, but the Agent's dark
			// layer is a class: under `system` nothing would restyle its half of
			// the document unless something re-stamps it from here.
			media.addEventListener("change", () => {
				if (readTheme() !== "system") return;
				restyle("system");
				for (const each of listeners) each();
			});
		}
	}
	return () => {
		listeners.delete(listener);
	};
}

export function useTheme(): Snapshot & { setTheme: (next: Theme) => void; cycle: () => void } {
	const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

	const setTheme = useCallback((next: Theme) => {
		writeTheme(next);
	}, []);

	const cycle = useCallback(() => {
		setTheme(THEMES[(THEMES.indexOf(readTheme()) + 1) % THEMES.length]);
	}, [setTheme]);

	return { ...state, setTheme, cycle };
}
